import { isApiEnabled } from './config';
import { waitForDeviceSession } from './device-session';
import { getProfile } from './profile';

import type { ProfileRestoreDeps, ProfileView } from './types';

/** 게이트가 스플래시를 붙들 수 있는 상한. 넘으면 서버를 포기하고 초기 설정을 연다. */
export const PROFILE_RESTORE_TIMEOUT_MS = 2_000;

const defaultDeps = (): ProfileRestoreDeps => ({
  enabled: isApiEnabled,
  get: getProfile,
  awaitSession: waitForDeviceSession,
  timeoutMs: PROFILE_RESTORE_TIMEOUT_MS,
});

/**
 * 서버 프로필을 한 번 읽어 온다 — **로컬에 프로필이 없을 때만** 부르는 복원 경로다.
 *
 * 실패도 지연도 `null` 이다. 이 호출이 화면을 여는 길목에 있어서, 못 읽었다는 사실보다
 * **앱이 열린다는 사실**이 중요하다. 서버가 프로필을 lazy 생성하므로 404 는 오지 않고,
 * 아직 아무것도 답하지 않은 사람에게는 빈 뷰가 온다 — 완성 여부 판정은 호출자 몫이다.
 *
 * **기기 세션을 먼저 기다린다.** 세션 없이 보낸 요청은 서명이 없어 `403` 이고, 그 403 은
 * 재시도로도 안 풀린다. 기다림과 요청이 같은 `timeoutMs` 안에 들어가므로 게이트가 붙드는
 * 시간은 그대로다 — 대신 상한 직전에 세션이 서면 요청은 나가고 답은 버려져 `/setup` 으로
 * 간다. 총 상한을 지키는 대가이고, 그 사람은 설문을 한 번 더 하게 된다.
 */
export const restoreProfile = async (
  deps: ProfileRestoreDeps = defaultDeps(),
): Promise<ProfileView | null> => {
  if (!deps.enabled()) {
    return null;
  }
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<null>((resolve) => {
    timer = setTimeout(() => resolve(null), deps.timeoutMs);
  });
  const read = async (): Promise<ProfileView | null> =>
    (await deps.awaitSession(deps.timeoutMs)) ? deps.get() : null;
  try {
    return await Promise.race([read(), timeout]);
  } catch {
    return null;
  } finally {
    clearTimeout(timer);
  }
};
